// ============================================================
// CLUBHUB – Das Einstellungs-Register
// ============================================================
// Jede Einstellung des Vereins steht hier — einmal, als Objekt.
// Daraus entstehen ohne weiteres Zutun: Formular, Pruefung,
// Aenderungsleiste, Speichern, Rechtepruefung, Suche, Tiefenlink und
// „Standard wiederherstellen“. Was hier nicht steht, gibt es nicht.
//
// Die Gliederung spiegelt die Hauptnavigation (`lib/navigation.ts`):
// wer die Mahnstufen sucht, sucht sie unter Finanzen, die Vorgaben
// fuer Termine unter Kalender. Hergeleitet aus dem ClubDesk-Dialog
// „Einstellungen: Verein“ und bewusst ohne dessen Tarifsperren,
// Lizenz- und Quotenseiten (`notes/einstellungen-konzept.md`).
// ============================================================

import type { Permission } from "@/lib/permissions";
import { RW, wert } from "@/lib/rechtswerte";
import type {
  SettingsArea,
  SettingsField,
  SettingsTab,
  SettingsValues,
} from "./types";

// ── Kleine Helfer fuer die Vorschauen ───────────────────────

function asNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value.replace(",", "."));
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function asText(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function germanDate(date: Date): string {
  return date.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function plusDays(today: Date, days: number): string {
  const date = new Date(today);
  date.setDate(date.getDate() + days);
  return germanDate(date);
}

/** Eine Nummer aus Praefix, Zaehler und Stellenzahl: „M-“ + 127 + 4 → „M-0127“. */
export function baueNummer(prefix: string, next: number, stellen: number): string {
  return `${prefix}${String(Math.max(0, Math.trunc(next))).padStart(Math.max(1, stellen), "0")}`;
}

const MONATE = [
  "Januar", "Februar", "März", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember",
];

export const MONATS_OPTIONEN = MONATE.map((label, i) => ({ value: String(i + 1), label }));

// ── Rechte-Kurzformen ───────────────────────────────────────

const VERWALTEN: readonly Permission[] = ["settings.manage"];
const BENUTZER: readonly Permission[] = ["users.manage"];
const API: readonly Permission[] = ["api.manage"];
const FINANZEN_SEHEN: readonly Permission[] = ["settings.manage", "finance.read"];
const FINANZEN_AENDERN: readonly Permission[] = ["finance.write"];

// ── Verein ──────────────────────────────────────────────────

const vereinAllgemein: SettingsField[] = [
  {
    key: "verein.name",
    type: "text",
    label: "Name",
    source: "club",
    column: "name",
    hint: "So, wie er im Vereinsregister steht. Er steht auf Rechnungen, Zuwendungsbestätigungen und im Kopf jeder Mail.",
    check: { required: true, maxLength: 160 },
  },
  {
    key: "verein.kurzname",
    type: "text",
    label: "Kurzname",
    source: "club",
    column: "shortName",
    hint: "Für die Leiste, den Vereins-Umschalter und den Betreff von Serienmails.",
    placeholder: "Grünspitz Allstars",
    check: { maxLength: 60 },
  },
  {
    key: "verein.rechtsform",
    type: "select",
    label: "Rechtsform",
    source: "club",
    column: "legalForm",
    default: "e. V.",
    options: [
      { value: "e. V.", label: "Eingetragener Verein (e. V.)" },
      { value: "nicht eingetragen", label: "Nicht eingetragener Verein" },
      { value: "gGmbH", label: "Gemeinnützige GmbH" },
      { value: "Stiftung", label: "Stiftung" },
      { value: "Verband", label: "Verband" },
    ],
  },
  {
    key: "verein.gruendung",
    type: "date",
    label: "Gegründet am",
    source: "club",
    column: "foundedAt",
    hint: "Für das Vereinsjubiläum auf der Übersicht.",
    preview: (values, today) => {
      const text = asText(values["verein.gruendung"]);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return null;
      const jahre = today.getFullYear() - Number(text.slice(0, 4));
      return jahre > 0 ? `${jahre} Jahre Vereinsgeschichte` : null;
    },
  },
  {
    key: "verein.registergericht",
    type: "text",
    label: "Registergericht",
    source: "club",
    column: "registerCourt",
    section: "Vereinsregister",
    placeholder: "Amtsgericht München",
    check: { maxLength: 80 },
  },
  {
    key: "verein.registernummer",
    type: "text",
    label: "Registernummer",
    source: "club",
    column: "registerNumber",
    placeholder: "VR 123456",
    hint: "Pflichtangabe im Impressum der Vereinsseite und auf Briefen.",
    check: { maxLength: 30, pattern: "^(VR|GnR|HRB|HRA)?\\s?\\d", patternHint: "Etwa „VR 123456“." },
  },
  {
    key: "verein.akzentfarbe",
    type: "color",
    label: "Vereinsfarbe",
    source: "club",
    column: "accentColor",
    section: "Auftritt",
    hint: "Färbt Vereinsseite, Mitgliederbereich und den Kopf von Serienmails. Die Verwaltung selbst bleibt in den Hausfarben.",
  },
];

const vereinAnschrift: SettingsField[] = [
  { key: "verein.strasse", type: "text", label: "Straße und Nummer", source: "club", column: "street", section: "Anschrift", check: { maxLength: 120 } },
  { key: "verein.adresszusatz", type: "text", label: "Adresszusatz", source: "setting", placeholder: "c/o, Vereinsheim, Postfach", check: { maxLength: 120 } },
  { key: "verein.plz", type: "text", label: "PLZ", source: "club", column: "zip", check: { maxLength: 10, pattern: "^[0-9A-Za-z -]{3,10}$", patternHint: "Etwa „80689“." } },
  { key: "verein.ort", type: "text", label: "Ort", source: "club", column: "city", check: { maxLength: 80 } },
  {
    key: "verein.land",
    type: "select",
    label: "Land",
    source: "club",
    column: "country",
    default: "DE",
    options: [
      { value: "DE", label: "Deutschland" },
      { value: "AT", label: "Österreich" },
      { value: "CH", label: "Schweiz" },
      { value: "LU", label: "Luxemburg" },
    ],
    hint: "Bestimmt Feiertage, Zahlungsverkehr (SEPA oder QR-Rechnung) und die Vorlagen der Zuwendungsbestätigung.",
  },
  { key: "verein.email", type: "text", label: "E-Mail", source: "club", column: "email", section: "Erreichbarkeit", placeholder: "vorstand@verein.de", check: { email: true, maxLength: 160 } },
  { key: "verein.telefon", type: "text", label: "Telefon", source: "club", column: "phone", check: { phone: true, maxLength: 40 } },
  { key: "verein.website", type: "text", label: "Website", source: "club", column: "website", placeholder: "https://", check: { url: true, maxLength: 200 } },
  {
    key: "verein.ansprechperson",
    type: "select",
    label: "Ansprechperson",
    source: "setting",
    hint: "Wer bei Rückfragen von Mitgliedern, Verband und Bank genannt wird. Zur Auswahl steht der Vorstand.",
    options: [{ value: "", label: "Niemand Bestimmtes" }],
    optionsFrom: "vorstand",
  },
];

const vereinGemeinnuetzig: SettingsField[] = [
  {
    key: "verein.gemeinnuetzig",
    type: "switch",
    label: "Gemeinnützig",
    source: "club",
    column: "isNonProfit",
    default: true,
    hint: "Nur mit gültigem Freistellungsbescheid darf der Verein Zuwendungsbestätigungen ausstellen.",
  },
  { key: "verein.steuernummer", type: "text", label: "Steuernummer", source: "club", column: "taxNumber", showIf: "verein.gemeinnuetzig", placeholder: "143/216/50000", check: { maxLength: 30 } },
  { key: "verein.finanzamt", type: "text", label: "Finanzamt", source: "club", column: "taxOffice", showIf: "verein.gemeinnuetzig", placeholder: "Finanzamt München", check: { maxLength: 80 } },
  {
    key: "verein.freistellung_datum",
    type: "date",
    label: "Freistellungsbescheid vom",
    source: "club",
    column: "nonProfitNoticeDate",
    showIf: "verein.gemeinnuetzig",
    hint: "Der Bescheid gilt fünf Jahre. Danach stellt clubhub keine Zuwendungsbestätigungen mehr aus, bis ein neuer Bescheid eingetragen ist.",
    preview: (values, today) => {
      const text = asText(values["verein.freistellung_datum"]);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return null;
      const ablauf = new Date(`${text}T00:00:00`);
      ablauf.setFullYear(ablauf.getFullYear() + 5);
      return ablauf < today
        ? `Abgelaufen seit ${germanDate(ablauf)} — bitte den neuen Bescheid eintragen.`
        : `Gültig bis ${germanDate(ablauf)}.`;
    },
  },
  {
    key: "verein.zweck",
    type: "multiline",
    label: "Satzungszweck",
    source: "club",
    column: "purpose",
    showIf: "verein.gemeinnuetzig",
    rows: 3,
    placeholder: "Förderung des Sports (§ 52 Abs. 2 Nr. 21 AO)",
    hint: "Steht wörtlich auf jeder Zuwendungsbestätigung.",
    check: { maxLength: 500 },
  },
];

const vereinBank: SettingsField[] = [
  { key: "verein.iban", type: "text", label: "IBAN", source: "club", column: "iban", placeholder: "DE00 0000 0000 0000 0000 00", hint: "Steht auf Rechnungen und ist das Konto, von dem Lastschriften eingezogen werden.", check: { iban: true } },
  { key: "verein.bic", type: "text", label: "BIC", source: "club", column: "bic", check: { bic: true } },
  { key: "verein.bank", type: "text", label: "Bank", source: "club", column: "bankName", check: { maxLength: 80 } },
  {
    key: "verein.glaeubiger_id",
    type: "text",
    label: "Gläubiger-ID",
    source: "club",
    column: "creditorId",
    section: "SEPA-Lastschrift",
    placeholder: "DE98ZZZ09999999999",
    hint: "Die Bundesbank vergibt sie kostenlos. Ohne sie gibt es keine Lastschrift — Beitragsläufe gehen dann nur als Rechnung hinaus.",
    check: { creditorId: true },
  },
];

// ── Mitglieder ──────────────────────────────────────────────

const mitgliederNummern: SettingsField[] = [
  {
    key: "mitglieder.nummer_praefix",
    type: "text",
    label: "Präfix",
    source: "setting",
    section: "Mitgliedsnummer",
    default: "",
    placeholder: "M-",
    hint: "Steht vor jeder Mitgliedsnummer. Leer lassen für reine Zahlen.",
    check: { maxLength: 10 },
    preview: (values) => {
      const next = asNumber(values["mitglieder.nummer_naechste"]) ?? 1;
      const stellen = asNumber(values["mitglieder.nummer_stellen"]) ?? 4;
      return `${baueNummer(asText(values["mitglieder.nummer_praefix"]), next, stellen)} — so heißt das nächste neue Mitglied.`;
    },
  },
  { key: "mitglieder.nummer_naechste", type: "number", label: "Nächste Nummer", source: "setting", default: 1, noReset: true, hint: "Kleiner als die höchste vergebene Nummer lässt clubhub sie nicht werden.", check: { required: true, integer: true, min: 1, max: 9999999 } },
  { key: "mitglieder.nummer_stellen", type: "number", label: "Stellen", source: "setting", default: 4, hint: "Mit führenden Nullen aufgefüllt — 4 ergibt 0042.", check: { required: true, integer: true, min: 1, max: 8 } },
  {
    key: "mitglieder.nummer_automatisch",
    type: "switch",
    label: "Automatisch vergeben",
    source: "setting",
    default: true,
    hint: "Aus: die Nummer wird beim Anlegen von Hand eingetragen (etwa, wenn der Verband sie vergibt).",
  },
  {
    key: "mitglieder.kuendigungsfrist",
    type: "select",
    label: "Kündigungsfrist",
    source: "setting",
    section: "Aufnahme und Austritt",
    default: "3m_jahresende",
    options: [
      { value: "keine", label: "Jederzeit zum Monatsende" },
      { value: "1m_jahresende", label: "Ein Monat zum Jahresende" },
      { value: "3m_jahresende", label: "Drei Monate zum Jahresende" },
      { value: "6m_jahresende", label: "Sechs Monate zum Jahresende" },
      { value: "3m_halbjahr", label: "Drei Monate zum Halbjahr" },
    ],
    hint: "Wie in der Satzung. Daraus rechnet clubhub das Austrittsdatum, wenn eine Kündigung eingeht.",
    preview: (values, today) => {
      const frist = asText(values["mitglieder.kuendigungsfrist"]);
      const monate = { keine: 0, "1m_jahresende": 1, "3m_jahresende": 3, "6m_jahresende": 6, "3m_halbjahr": 3 }[frist];
      if (monate === undefined) return null;
      const stichtag = new Date(today);
      stichtag.setMonth(stichtag.getMonth() + monate);
      let ende: Date;
      if (frist === "keine") ende = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      else if (frist === "3m_halbjahr")
        ende = stichtag.getMonth() < 6 ? new Date(stichtag.getFullYear(), 5, 30) : new Date(stichtag.getFullYear(), 11, 31);
      else ende = new Date(stichtag.getFullYear(), 11, 31);
      return `Wer heute kündigt, ist Mitglied bis ${germanDate(ende)}.`;
    },
  },
  {
    key: "mitglieder.aufnahme_freigabe",
    type: "switch",
    label: "Aufnahme bestätigen",
    source: "setting",
    default: true,
    hint: "Online-Aufnahmeanträge landen erst als Interessent und werden Mitglied, wenn der Vorstand sie bestätigt.",
  },
  {
    key: "mitglieder.jubilaeen",
    type: "text",
    label: "Jubiläen",
    source: "setting",
    section: "Übersicht",
    default: "10, 25, 40, 50, 60",
    suffix: "Jahre",
    hint: "Welche Mitgliedsjahre auf der Übersicht und im Vereinskalender als Jubiläum erscheinen.",
    check: { pattern: "^\\s*\\d{1,3}(\\s*,\\s*\\d{1,3})*\\s*$", patternHint: "Zahlen mit Komma getrennt, etwa „10, 25, 50“." },
  },
  {
    key: "mitglieder.geburtstage_tage",
    type: "number",
    label: "Geburtstage voraus",
    source: "setting",
    default: 14,
    suffix: "Tage",
    hint: "Wie weit die Übersicht nach vorne schaut. Gezeigt wird nur, wer der Veröffentlichung zugestimmt hat.",
    check: { integer: true, min: 0, max: 90 },
  },
];

// ── Kalender ────────────────────────────────────────────────

const kalenderVorgaben: SettingsField[] = [
  {
    key: "kalender.einladung_beim_speichern",
    type: "switch",
    label: "Einladung beim Speichern",
    source: "setting",
    section: "Einladung",
    default: false,
    hint: "An: wer einen neuen Termin speichert, wird gefragt, ob die Teilnehmer jetzt eingeladen werden — vorausgewählt mit Ja.",
  },
  {
    key: "kalender.absender",
    type: "select",
    label: "Absender der Einladung",
    source: "setting",
    default: "verein",
    options: [
      { value: "verein", label: "Der Verein (Absender aus Kommunikation)" },
      { value: "person", label: "Wer den Termin anlegt" },
      { value: "noreply", label: "Keine Antwortadresse (noreply)" },
    ],
  },
  {
    key: "kalender.erinnerung",
    type: "select",
    label: "Erinnerung",
    source: "setting",
    default: "1d",
    options: [
      { value: "keine", label: "Keine" },
      { value: "2h", label: "Zwei Stunden vorher" },
      { value: "1d", label: "Einen Tag vorher" },
      { value: "2d", label: "Zwei Tage vorher" },
      { value: "7d", label: "Eine Woche vorher" },
    ],
    hint: "Geht nur an Teilnehmer, die zugesagt oder noch nicht geantwortet haben. Wer keine Erinnerungen will, stellt das in seinem Konto selbst ab (Mein Konto › Benachrichtigungen).",
  },
  {
    key: "kalender.zusagen",
    type: "switch",
    label: "Zu- und Absagen",
    source: "setting",
    section: "Rückmeldung",
    default: true,
    hint: "Teilnehmer antworten per Klick aus der Mail, in der App oder im Mitgliederbereich — ohne Anmeldung.",
  },
  { key: "kalender.vielleicht", type: "switch", label: "„Vielleicht“ erlauben", source: "setting", default: true, showIf: "kalender.zusagen" },
  {
    key: "kalender.vorab_zugesagt",
    type: "switch",
    label: "Alle vorab zugesagt",
    source: "setting",
    default: false,
    showIf: "kalender.zusagen",
    hint: "Für Pflichttermine wie das Training: wer nicht absagt, gilt als dabei.",
  },
  {
    key: "kalender.frist",
    type: "select",
    label: "Rückmeldung bis",
    source: "setting",
    default: "beginn",
    showIf: "kalender.zusagen",
    options: [
      { value: "beginn", label: "Terminbeginn" },
      { value: "2h", label: "Zwei Stunden vorher" },
      { value: "1d", label: "Einen Tag vorher" },
      { value: "2d", label: "Zwei Tage vorher" },
      { value: "7d", label: "Eine Woche vorher" },
    ],
  },
  {
    key: "kalender.zusagen_sichtbar",
    type: "select",
    label: "Wer sieht die Zusagen",
    source: "setting",
    default: "anzahl",
    showIf: "kalender.zusagen",
    options: [
      { value: "verwaltung", label: "Nur die Verwaltung" },
      { value: "anzahl", label: "Teilnehmer sehen die Anzahl" },
      { value: "namen", label: "Teilnehmer sehen die Namen" },
    ],
    hint: "Namen zeigen hilft beim Fahrgemeinschaften-Bilden, ist aber eine Weitergabe personenbezogener Daten an andere Mitglieder.",
  },
  {
    key: "kalender.ohne_teilnehmer_alle",
    type: "switch",
    label: "Ohne Teilnehmer gilt für alle",
    source: "setting",
    section: "Darstellung",
    default: true,
    hint: "Ein Termin ohne Gruppe oder Teilnehmer erscheint bei allen Mitgliedern — etwa die Mitgliederversammlung.",
  },
  { key: "kalender.dauer_minuten", type: "number", label: "Übliche Dauer", source: "setting", default: 90, suffix: "Minuten", hint: "Vorbelegt, wenn ein neuer Termin nur eine Anfangszeit bekommt.", check: { integer: true, min: 15, max: 1440 } },
  {
    key: "kalender.abo",
    type: "switch",
    label: "Kalender-Abo",
    source: "setting",
    default: true,
    hint: "Mitglieder abonnieren ihre Termine in Apple-, Google- oder Outlook-Kalender. Jede Person bekommt eine eigene, widerrufbare Adresse.",
  },
];

// ── Finanzen ────────────────────────────────────────────────

const finanzenBuchhaltung: SettingsField[] = [
  {
    key: "finanzen.buchhaltungsart",
    type: "select",
    label: "Buchführung",
    source: "club",
    column: "accountingMode",
    default: "EUR",
    options: [
      { value: "CASHBOOK", label: "Kassenbuch" },
      { value: "EUR", label: "Einnahmen-Überschuss-Rechnung" },
      { value: "DOUBLE_ENTRY", label: "Doppelte Buchführung" },
    ],
    hint: "Die meisten Vereine kommen mit der Einnahmen-Überschuss-Rechnung aus. Ein Wechsel gilt ab dem nächsten Geschäftsjahr.",
  },
  {
    key: "finanzen.geschaeftsjahr_beginn",
    type: "select",
    label: "Geschäftsjahr beginnt im",
    source: "club",
    column: "fiscalYearStart",
    default: "1",
    options: MONATS_OPTIONEN,
    preview: (values, today) => {
      const monat = asNumber(values["finanzen.geschaeftsjahr_beginn"]) ?? 1;
      const beginn = new Date(today.getFullYear(), monat - 1, 1);
      if (beginn > today) beginn.setFullYear(beginn.getFullYear() - 1);
      const ende = new Date(beginn.getFullYear() + 1, beginn.getMonth(), 0);
      return `Laufendes Geschäftsjahr: ${germanDate(beginn)} bis ${germanDate(ende)}.`;
    },
  },
  { key: "finanzen.kostenstellen", type: "switch", label: "Kostenstellen", source: "setting", default: true, hint: "Buchungen lassen sich Abteilungen, Mannschaften oder Veranstaltungen zuordnen — für die Auswertung je Sparte." },
  {
    key: "finanzen.sphaeren",
    type: "switch",
    label: "Steuerliche Sphären",
    source: "setting",
    default: true,
    hint: "Jede Buchung trägt ihre Sphäre (ideell, Vermögensverwaltung, Zweckbetrieb, wirtschaftlicher Geschäftsbetrieb). Das Finanzamt fragt danach.",
  },
  {
    key: "finanzen.umsatzsteuer",
    type: "switch",
    label: "Umsatzsteuerpflichtig",
    source: "setting",
    default: false,
    hint: `Aus, solange der Verein unter die Kleinunternehmerregelung fällt (§ 19 UStG: Umsatz im Vorjahr höchstens ${wert(RW.UST_KLEINUNTERNEHMER_VORJAHR_EUR).toLocaleString("de-DE")} €, im laufenden Jahr voraussichtlich höchstens ${wert(RW.UST_KLEINUNTERNEHMER_LAUFEND_EUR).toLocaleString("de-DE")} €). An: Rechnungen weisen Umsatzsteuer aus.`,
  },
];

const finanzenRechnungen: SettingsField[] = [
  {
    key: "finanzen.rechnung_praefix",
    type: "text",
    label: "Präfix",
    source: "setting",
    section: "Rechnungsnummer",
    default: "R{JJJJ}-",
    variables: ["{JJJJ}", "{JJ}"],
    hint: "{JJJJ} wird zum Jahr. Der Zähler läuft über das Jahr weiter, damit keine Nummer zweimal vergeben wird.",
    check: { maxLength: 16 },
    preview: (values, today) => {
      const prefix = asText(values["finanzen.rechnung_praefix"])
        .replaceAll("{JJJJ}", String(today.getFullYear()))
        .replaceAll("{JJ}", String(today.getFullYear()).slice(2));
      const next = asNumber(values["finanzen.rechnung_naechste"]) ?? 1;
      return `${baueNummer(prefix, next, 4)} — so heißt die nächste Rechnung.`;
    },
  },
  { key: "finanzen.rechnung_naechste", type: "number", label: "Nächste Nummer", source: "setting", default: 1, noReset: true, hint: "Kleiner als die höchste Rechnungsnummer des Jahres lässt clubhub sie nicht werden — eine Nummer darf es nur einmal geben.", check: { required: true, integer: true, min: 1, max: 9999999 } },
  {
    key: "finanzen.zahlungsziel_tage",
    type: "number",
    label: "Zahlungsziel",
    source: "setting",
    section: "Zahlung",
    default: 14,
    suffix: "Tage",
    check: { required: true, integer: true, min: 0, max: 120 },
    preview: (values, today) => {
      const tage = asNumber(values["finanzen.zahlungsziel_tage"]);
      return tage === null ? null : `Eine Rechnung von heute ist fällig am ${plusDays(today, tage)}.`;
    },
  },
  {
    key: "finanzen.mahnstufen",
    type: "number",
    label: "Mahnstufen",
    source: "setting",
    section: "Mahnwesen",
    default: 2,
    hint: "Zahlungserinnerung zählt nicht mit. Nach der letzten Stufe schlägt clubhub vor, das Mitglied an den Vorstand zu geben.",
    check: { integer: true, min: 0, max: 3 },
  },
  {
    key: "finanzen.mahnabstand_tage",
    type: "number",
    label: "Abstand",
    source: "setting",
    default: 14,
    suffix: "Tage",
    check: { integer: true, min: 7, max: 60 },
    preview: (values, today) => {
      const ziel = asNumber(values["finanzen.zahlungsziel_tage"]) ?? 14;
      const abstand = asNumber(values["finanzen.mahnabstand_tage"]) ?? 14;
      return `Erinnerung am ${plusDays(today, ziel + 3)}, erste Mahnung am ${plusDays(today, ziel + 3 + abstand)}.`;
    },
  },
  { key: "finanzen.mahngebuehr", type: "amount", label: "Mahngebühr", source: "setting", speicherEinheit: "euro", default: 0, suffix: "€", hint: "Ab der ersten Mahnung. Viele Vereine verzichten darauf.", check: { min: 0, max: 5000 } },
  {
    key: "finanzen.rechnung_fusstext",
    type: "multiline",
    label: "Fußtext",
    source: "setting",
    section: "Text",
    rows: 3,
    default: "Vielen Dank für deine Unterstützung des Vereins.",
    variables: ["{Vorname}", "{Faelligkeit}", "{Betrag}"],
    check: { maxLength: 600 },
  },
];

const finanzenSepa: SettingsField[] = [
  {
    key: "finanzen.sepa_vorlauf_tage",
    type: "number",
    label: "Vorlauf der Lastschrift",
    source: "setting",
    default: 5,
    suffix: "Bankarbeitstage",
    hint: "So viele Bankarbeitstage vor Fälligkeit geht die Datei an die Bank. Die Bank verlangt mindestens einen.",
    check: { integer: true, min: 1, max: 14 },
  },
  {
    key: "finanzen.vorabinfo_tage",
    type: "number",
    label: "Vorabankündigung",
    source: "setting",
    default: wert(RW.SEPA_VORABANKUENDIGUNG_TAGE),
    suffix: "Tage",
    hint: `So lange vorher erfahren Mitglieder Betrag und Datum des Einzugs (Pre-Notification). Vorgabe ${wert(RW.SEPA_VORABANKUENDIGUNG_TAGE)} Tage; kürzer nur, wenn es im Mandat so vereinbart ist — der Mandatstext übernimmt diesen Wert.`,
    check: { integer: true, min: 1, max: 30 },
  },
  {
    key: "finanzen.mandat_praefix",
    type: "text",
    label: "Mandatsreferenz",
    source: "setting",
    default: "GA-",
    hint: "Vor die Mitgliedsnummer gesetzt. Eine einmal vergebene Referenz ändert sich nie.",
    check: { maxLength: 20, pattern: "^[A-Za-z0-9+?/:().,' -]*$", patternHint: "Nur Buchstaben, Ziffern und + ? / - : ( ) . , '" },
    preview: (values) => `${asText(values["finanzen.mandat_praefix"])}0042 — Referenz für Mitglied 42.`,
  },
  {
    key: "finanzen.beitrag_anteilig",
    type: "switch",
    label: "Anteiliger Beitrag",
    source: "setting",
    section: "Beitragslauf",
    default: true,
    hint: "Wer im Laufe des Jahres eintritt, zahlt im ersten Jahr nur die restlichen Monate.",
  },
];

const finanzenSpenden: SettingsField[] = [
  {
    key: "finanzen.bescheinigung",
    type: "select",
    label: "Nach einer Spende",
    source: "setting",
    default: "zuwendungsbestaetigung",
    options: [
      { value: "keine", label: "Nichts" },
      { value: "dankesbrief", label: "Dankesbrief" },
      { value: "zuwendungsbestaetigung", label: "Zuwendungsbestätigung" },
    ],
    hint: "Zuwendungsbestätigungen gibt es nur, solange der Verein als gemeinnützig eingetragen ist (Verein › Gemeinnützigkeit).",
  },
  {
    key: "finanzen.sammelbestaetigung",
    type: "switch",
    label: "Sammelbestätigung",
    source: "setting",
    default: true,
    hint: "Wer mehrmals im Jahr spendet, bekommt im Januar eine Bestätigung über alles statt vieler einzelner.",
  },
  { key: "finanzen.unterzeichner", type: "text", label: "Unterzeichnet von", source: "setting", placeholder: "Name und Amt", hint: "Steht unter der Bestätigung; die Unterschrift selbst ist eingescannt oder entfällt bei maschineller Erstellung.", check: { maxLength: 120 } },
  { key: "finanzen.spenden_ab", type: "amount", label: "Vereinfachter Nachweis bis", source: "setting", default: wert(RW.SPENDEN_VEREINFACHTER_NACHWEIS_EUR) * 100, suffix: "€", hint: `Bis zu diesem Betrag reicht dem Finanzamt der Kontoauszug (§ 50 Abs. 4 EStDV, höchstens ${wert(RW.SPENDEN_VEREINFACHTER_NACHWEIS_EUR).toLocaleString("de-DE")} €). Darüber stellt clubhub immer eine Bestätigung aus.`, check: { min: 0, max: wert(RW.SPENDEN_VEREINFACHTER_NACHWEIS_EUR) * 100 } },
];

// ── Kommunikation ───────────────────────────────────────────

const kommunikationVersand: SettingsField[] = [
  { key: "kommunikation.absendername", type: "text", label: "Absendername", source: "setting", hint: "Steht im Posteingang vor dem Betreff. Leer: der Kurzname des Vereins.", check: { maxLength: 80 } },
  {
    key: "kommunikation.absender",
    type: "text",
    label: "Absenderadresse",
    source: "setting",
    placeholder: "info@verein.de",
    hint: "Leer: Versand über die clubhub-Adresse des Vereins. Eine eigene Domain braucht einen SPF- und DKIM-Eintrag, den clubhub hier prüft.",
    check: { email: true, maxLength: 160 },
  },
  { key: "kommunikation.antwort_an", type: "text", label: "Antworten gehen an", source: "setting", placeholder: "vorstand@verein.de", check: { email: true, maxLength: 160 } },
  {
    key: "kommunikation.zweite_adresse",
    type: "switch",
    label: "Auch an zweite Adresse",
    source: "setting",
    default: false,
    hint: "Serienmails gehen zusätzlich an die zweite E-Mail-Adresse eines Mitglieds.",
  },
  {
    key: "kommunikation.archiv",
    type: "select",
    label: "Gesendete Mails aufbewahren",
    source: "setting",
    section: "Archiv",
    default: "3j",
    options: [
      { value: "1j", label: "Ein Jahr" },
      { value: "3j", label: "Drei Jahre" },
      { value: "10j", label: "Zehn Jahre" },
      { value: "immer", label: "Unbegrenzt" },
    ],
    hint: "Jede Serienmail bleibt mit Empfängern und Zustellstatus im Archiv nachvollziehbar.",
  },
  {
    key: "kommunikation.gruppenadressen",
    type: "select",
    label: "Gruppenadressen",
    source: "setting",
    section: "Vereinsadressen",
    default: "verwaltung",
    options: [
      { value: "aus", label: "Keine Gruppenadressen" },
      { value: "verwaltung", label: "Nur die Verwaltung darf an Gruppen schreiben" },
      { value: "mitglieder", label: "Mitglieder der Gruppe dürfen schreiben" },
      { value: "alle", label: "Jeder darf schreiben (auch von außen)" },
    ],
    hint: "Eine Mail an die Adresse einer Gruppe (etwa „erste@…“) geht an alle ihre Mitglieder.",
  },
];

const kommunikationSignatur: SettingsField[] = [
  {
    key: "kommunikation.signatur",
    type: "multiline",
    label: "Vereins-Signatur",
    source: "setting",
    rows: 6,
    default: "{Vorname} {Nachname}\n{Funktion}\n{Verein}",
    variables: ["{Vorname}", "{Nachname}", "{Funktion}", "{Verein}", "{Website}"],
    hint: "Unter jede Mail aus clubhub gesetzt, wenn die Person keine eigene Signatur hat (Mein Konto › Profil).",
    check: { maxLength: 1200 },
  },
];

// ── Website ─────────────────────────────────────────────────

const websiteMitgliederbereich: SettingsField[] = [
  {
    key: "website.mitgliederbereich",
    type: "switch",
    label: "Mitgliederbereich",
    source: "setting",
    default: true,
    hint: "Mitglieder melden sich an und sehen ihre Termine, Dokumente, Beiträge und ihre eigenen Daten.",
  },
  {
    key: "website.selbst_aendern",
    type: "select",
    label: "Eigene Daten ändern",
    source: "setting",
    default: "freigabe",
    showIf: "website.mitgliederbereich",
    options: [
      { value: "nein", label: "Nur ansehen" },
      { value: "freigabe", label: "Ändern, Verwaltung bestätigt" },
      { value: "direkt", label: "Ändern, gilt sofort" },
    ],
    hint: "Adresse, Telefon, Bankverbindung, Einwilligungen. Eine neue IBAN braucht immer ein neues Lastschriftmandat.",
  },
  { key: "website.geburtstage", type: "switch", label: "Geburtstage zeigen", source: "setting", default: true, showIf: "website.mitgliederbereich", hint: "Nur von Mitgliedern, die zugestimmt haben." },
  {
    key: "website.mitgliederliste",
    type: "select",
    label: "Mitgliederliste",
    source: "setting",
    default: "gruppe",
    showIf: "website.mitgliederbereich",
    options: [
      { value: "nein", label: "Nicht zeigen" },
      { value: "gruppe", label: "Nur die eigene Gruppe" },
      { value: "alle", label: "Alle Mitglieder" },
    ],
    hint: "Gezeigt werden Name und Gruppe, Kontaktdaten nur, wenn die Person es freigibt.",
  },
];

// ── Zugriff ─────────────────────────────────────────────────

const zugriffAnmeldung: SettingsField[] = [
  {
    key: "zugriff.zwei_faktor",
    type: "select",
    label: "Zwei-Faktor-Anmeldung",
    source: "setting",
    default: "verwaltung",
    options: [
      { value: "freiwillig", label: "Freiwillig" },
      { value: "verwaltung", label: "Pflicht für Rollen mit Zugriff auf Mitglieder oder Finanzen" },
      { value: "alle", label: "Pflicht für alle" },
    ],
    hint: "Ein Passkey (Fingerabdruck, Gesicht) zählt als zweiter Faktor.",
  },
  { key: "zugriff.passkeys", type: "switch", label: "Passkeys", source: "setting", default: true, hint: "Anmelden ohne Passwort, mit dem Gerät." },
  { key: "zugriff.sitzung_tage", type: "number", label: "Angemeldet bleiben", source: "setting", default: 30, suffix: "Tage", hint: "Danach fragt clubhub erneut nach der Anmeldung. Verwaltungsrollen höchstens 14 Tage.", check: { integer: true, min: 1, max: 90 } },
  {
    key: "zugriff.zugang_anfordern",
    type: "switch",
    label: "Zugang selbst anfordern",
    source: "setting",
    section: "Einladung",
    default: true,
    hint: "Mitglieder mit hinterlegter E-Mail-Adresse holen sich ihren Zugang auf der Anmeldeseite selbst. Aus: nur per Einladung.",
  },
  { key: "zugriff.einladung_tage", type: "number", label: "Einladung gültig", source: "setting", default: 14, suffix: "Tage", check: { integer: true, min: 1, max: 60 } },
  {
    key: "zugriff.standardrolle",
    type: "select",
    label: "Rolle neuer Zugänge",
    source: "setting",
    default: "MEMBER",
    options: [
      { value: "MEMBER", label: "Mitglied" },
      { value: "READONLY", label: "Nur lesen" },
    ],
    hint: "Verwaltungsrollen vergibt immer eine Person mit Benutzerrecht, nie die Selbstanmeldung.",
  },
];

// ── Datenschutz ─────────────────────────────────────────────

const datenschutzFristen: SettingsField[] = [
  {
    key: "datenschutz.ehemalige_jahre",
    type: "number",
    label: "Ehemalige anonymisieren nach",
    source: "setting",
    default: 3,
    suffix: "Jahren",
    hint: `Nach dem Austritt. Buchungen bleiben ${wert(RW.AUFBEWAHRUNG_BUECHER_JAHRE)} Jahre, Rechnungen und andere Belege ${wert(RW.AUFBEWAHRUNG_BUCHUNGSBELEGE_JAHRE)} Jahre (§ 147 AO), aber ohne Namen und Anschrift.`,
    check: { integer: true, min: 1, max: 30 },
  },
  { key: "datenschutz.interessenten_monate", type: "number", label: "Interessenten löschen nach", source: "setting", default: 6, suffix: "Monaten", hint: "Aufnahmeanträge und Probetraining-Anfragen, aus denen keine Mitgliedschaft wurde.", check: { integer: true, min: 1, max: 36 } },
  { key: "datenschutz.protokoll_monate", type: "number", label: "Protokoll aufbewahren", source: "setting", default: 24, suffix: "Monate", check: { integer: true, min: 6, max: 120 } },
  {
    key: "datenschutz.automatisch",
    type: "switch",
    label: "Automatisch ausführen",
    source: "setting",
    default: false,
    hint: "Aus: clubhub schlägt jeden Monat vor, wer anonymisiert oder gelöscht wird, und wartet auf Bestätigung.",
  },
];

const datenschutzVerantwortlich: SettingsField[] = [
  { key: "datenschutz.beauftragter", type: "text", label: "Datenschutzbeauftragte(r)", source: "setting", hint: "Pflicht erst ab 20 Personen, die ständig mit Mitgliederdaten arbeiten — freiwillig aber sinnvoll.", check: { maxLength: 120 } },
  { key: "datenschutz.kontakt", type: "text", label: "Kontakt für Auskunft", source: "setting", placeholder: "datenschutz@verein.de", hint: "An diese Adresse gehen Anfragen auf Auskunft und Löschung aus dem Mitgliederbereich.", check: { email: true, maxLength: 160 } },
  { key: "datenschutz.erklaerung_url", type: "text", label: "Datenschutzerklärung", source: "setting", placeholder: "https://", check: { url: true, maxLength: 200 } },
  {
    key: "datenschutz.einwilligungen",
    type: "select",
    label: "Einwilligungen abfragen",
    source: "setting",
    section: "Einwilligungen",
    default: "aufnahme",
    options: [
      { value: "aufnahme", label: "Bei Aufnahme" },
      { value: "aufnahme_jaehrlich", label: "Bei Aufnahme und jährlich bestätigen" },
    ],
    hint: "Newsletter, Fotos, Geburtstag veröffentlichen — jede mit Datum und Weg festgehalten, jederzeit im Mitgliederbereich widerrufbar.",
  },
];

// ── Dateiablage ─────────────────────────────────────────────

const datenAblage: SettingsField[] = [
  {
    key: "ablage.anbieter",
    type: "select",
    label: "Speicherort",
    source: "storage",
    column: "driver",
    default: "HETZNER_S3",
    options: [
      { value: "HETZNER_S3", label: "Hetzner Object Storage (Vorgabe)" },
      { value: "S3", label: "Eigener S3-Speicher" },
      { value: "LOCAL", label: "Auf dem Server (nur Entwicklung)" },
    ],
    hint: "Dokumente, Bilder und Belege liegen verschlüsselt in Deutschland. Ein eigener S3-Speicher bleibt vollständig im Besitz des Vereins.",
  },
  { key: "ablage.endpunkt", type: "text", label: "Endpunkt", source: "storage", column: "endpoint", placeholder: "https://fsn1.your-objectstorage.com", check: { url: true, maxLength: 200 } },
  { key: "ablage.region", type: "text", label: "Region", source: "storage", column: "region", placeholder: "fsn1", check: { maxLength: 40 } },
  { key: "ablage.bucket", type: "text", label: "Bucket", source: "storage", column: "bucket", check: { maxLength: 63, pattern: "^[a-z0-9][a-z0-9.-]{1,61}[a-z0-9]$", patternHint: "Kleinbuchstaben, Ziffern, Punkt und Bindestrich, 3 bis 63 Zeichen." } },
  { key: "ablage.zugang", type: "text", label: "Zugangsschlüssel", source: "storage", column: "accessKeyId", check: { maxLength: 128 } },
  {
    key: "ablage.geheimnis",
    type: "secret",
    label: "Geheimer Schlüssel",
    source: "storage",
    column: "secretEncrypted",
    hint: "Wird verschlüsselt gespeichert und nie wieder angezeigt. Leer lassen, um den hinterlegten zu behalten.",
    check: { maxLength: 256 },
  },
];

// ── Mein Konto (eigene Seite, nicht in den Einstellungen) ───

const kontoProfil: SettingsField[] = [
  {
    key: "ich.signatur",
    type: "multiline",
    label: "Eigene Signatur",
    source: "user",
    column: "signature",
    rows: 4,
    variables: ["{Vorname}", "{Nachname}", "{Funktion}", "{Verein}"],
    hint: "Leer: die Vereins-Signatur aus Kommunikation.",
    check: { maxLength: 800 },
  },
];

const kontoBenachrichtigungen: SettingsField[] = [
  { key: "ich.anrede", type: "select", label: "Anrede in Mails", source: "user", default: "du", options: [{ value: "du", label: "Du" }, { value: "sie", label: "Sie" }], hint: "Wie clubhub dich in Benachrichtigungen anspricht." },
  { key: "ich.einladungen", type: "switch", label: "Termin-Einladungen", source: "user", section: "Per Mail", default: true },
  { key: "ich.erinnerungen", type: "switch", label: "Termin-Erinnerungen", source: "user", default: true },
  { key: "ich.info_mails", type: "switch", label: "Info-Mails des Vereins", source: "user", default: true, hint: "Rundschreiben und Newsletter. Rechnungen, Mahnungen und Einladungen zur Mitgliederversammlung kommen immer." },
  {
    key: "ich.zusammenfassung",
    type: "select",
    label: "Zusammenfassung",
    source: "user",
    default: "woechentlich",
    options: [
      { value: "aus", label: "Keine" },
      { value: "taeglich", label: "Täglich" },
      { value: "woechentlich", label: "Wöchentlich am Montag" },
    ],
    hint: "Neue Aushänge, anstehende Termine, offene Aufgaben — in einer Mail statt vieler.",
  },
];

// ── Die Bereiche ────────────────────────────────────────────

export const SETTINGS_AREAS: SettingsArea[] = [
  {
    key: "verein",
    title: "Verein",
    group: "verein",
    icon: "Building2",
    recht: VERWALTEN,
    tabs: [
      {
        key: "allgemein",
        title: "Allgemein",
        why: "Wer der Verein ist. Diese Angaben stehen auf Rechnungen, Zuwendungsbestätigungen, SEPA-Vorabinformationen und im Impressum der Vereinsseite.",
        fields: vereinAllgemein,
      },
      {
        key: "anschrift",
        title: "Anschrift und Kontakt",
        why: "Wohin Post, Bank und Verband schreiben — und wer bei Rückfragen genannt wird.",
        fields: vereinAnschrift,
      },
      {
        key: "gemeinnuetzigkeit",
        title: "Gemeinnützigkeit",
        why: "Ohne gültigen Freistellungsbescheid keine Zuwendungsbestätigung. clubhub rechnet die Frist mit und warnt rechtzeitig.",
        fields: vereinGemeinnuetzig,
      },
      {
        key: "bank",
        title: "Bank und SEPA",
        why: "Das Vereinskonto für Rechnungen und Beitragseinzug. Ohne Gläubiger-ID kein Lastschriftverfahren.",
        fields: vereinBank,
        recht: FINANZEN_SEHEN,
        aendern: FINANZEN_AENDERN,
      },
    ],
  },
  {
    key: "mitglieder",
    title: "Mitglieder",
    group: "bereiche",
    icon: "Users",
    recht: VERWALTEN,
    tabs: [
      {
        key: "status",
        title: "Mitgliedsstatus",
        why: "Welche Status es gibt, wie sie heißen und was aus ihnen folgt: wer als Mitglied zählt (Beiträge, Stimmrecht, Statistik) und wessen Jubiläen erscheinen.",
        panel: "status",
      },
      {
        key: "funktionen",
        title: "Funktionen",
        why: "Ämter im Verein — Vorsitz, Kasse, Jugendleitung. Eine Funktion vergibt keine Rechte (das tun Rollen), sie steht in Listen, Signaturen und im Vereinsregister-Auszug.",
        panel: "funktionen",
      },
      {
        key: "eigene-felder",
        title: "Eigene Felder",
        why: "Was der Verein zusätzlich über seine Mitglieder festhält — Trikotgröße, Lizenznummer, Ehrenamt —, ohne dass jemand die Masken umbauen muss.",
        note: "Einwilligungen (Newsletter, Fotos, Geburtstag) sind keine eigenen Felder, sondern fest eingebaut, mit Datum und Widerruf — anders als in ClubDesk.",
        panel: "eigene-felder",
      },
      {
        key: "aufnahme",
        title: "Aufnahme und Nummern",
        why: "Wie neue Mitglieder ihre Nummer bekommen, wann eine Kündigung wirkt und welche Jubiläen gefeiert werden.",
        fields: mitgliederNummern,
      },
    ],
  },
  {
    key: "kalender",
    title: "Kalender",
    group: "bereiche",
    icon: "CalendarDays",
    recht: VERWALTEN,
    tabs: [
      {
        key: "termintypen",
        title: "Termintypen",
        why: "Training, Spiel, Sitzung, Fest — jeder Typ mit eigener Farbe, damit der Kalender auf einen Blick lesbar ist.",
        panel: "termintypen",
      },
      {
        key: "vorgaben",
        title: "Vorgaben für Termine",
        why: "Was ein neuer Termin von selbst mitbringt: Einladung, Erinnerung, Zu- und Absage. Jeder Termin kann davon abweichen.",
        fields: kalenderVorgaben,
      },
    ],
  },
  {
    key: "finanzen",
    title: "Finanzen",
    group: "bereiche",
    icon: "Landmark",
    recht: FINANZEN_SEHEN,
    aendern: FINANZEN_AENDERN,
    tabs: [
      {
        key: "buchhaltung",
        title: "Buchhaltung",
        why: "Wie der Verein bucht. Daraus folgen Kontenrahmen, Jahresabschluss und die Auswertung für das Finanzamt.",
        fields: finanzenBuchhaltung,
      },
      {
        key: "rechnungen",
        title: "Rechnungen und Mahnwesen",
        why: "Nummer, Zahlungsziel und Mahnstufen für Beitrags- und freie Rechnungen. Keine Nummer wird zweimal vergeben.",
        fields: finanzenRechnungen,
      },
      {
        key: "sepa",
        title: "Beiträge und SEPA",
        why: "Wie Beiträge eingezogen werden: Vorlauf, Vorabankündigung und Mandatsreferenz. Die Gläubiger-ID steht unter Verein › Bank und SEPA.",
        fields: finanzenSepa,
      },
      {
        key: "spenden",
        title: "Spenden",
        why: "Was nach einer Spende passiert — vom Dankesbrief bis zur Zuwendungsbestätigung nach amtlichem Muster.",
        fields: finanzenSpenden,
      },
    ],
  },
  {
    key: "kommunikation",
    title: "Kommunikation",
    group: "bereiche",
    icon: "Mail",
    recht: VERWALTEN,
    tabs: [
      {
        key: "versand",
        title: "E-Mail-Versand",
        why: "Wer als Absender erscheint, wohin Antworten gehen und wie lange gesendete Mails nachvollziehbar bleiben.",
        fields: kommunikationVersand,
      },
      {
        key: "signatur",
        title: "Signatur",
        why: "Ein einheitlicher Abschluss für alle Mails aus clubhub. Platzhalter setzen Name und Funktion der sendenden Person ein.",
        fields: kommunikationSignatur,
      },
    ],
  },
  {
    key: "website",
    title: "Website",
    group: "bereiche",
    icon: "Globe",
    recht: VERWALTEN,
    tabs: [
      {
        key: "rubriken",
        title: "Rubriken",
        why: "Unter welchen Überschriften Beiträge auf der Vereinsseite erscheinen. In ClubDesk hießen sie „Beitrag-Typen für Webseite“.",
        panel: "rubriken",
      },
      {
        key: "mitgliederbereich",
        title: "Mitgliederbereich",
        why: "Was Mitglieder nach der Anmeldung sehen und selbst ändern dürfen — der Teil, der dem Vorstand die meiste Pflege abnimmt.",
        fields: websiteMitgliederbereich,
      },
    ],
  },
  {
    key: "zugriff",
    title: "Zugriff",
    group: "system",
    icon: "ShieldCheck",
    recht: BENUTZER,
    tabs: [
      {
        key: "benutzer",
        title: "Benutzer",
        why: "Wer sich anmelden kann und mit welcher Rolle. Jedes Mitglied kann einen Zugang bekommen — eingeladen oder selbst angefordert.",
        panel: "benutzer",
      },
      {
        key: "rollen",
        title: "Rollen und Rechte",
        why: "Was eine Rolle darf. Rollen vergeben Rechte, Funktionen nicht: die Kassenwartin braucht die Rolle „Kassenwart“, nicht nur das Amt.",
        panel: "rollen",
        note: "Abweichungen je Rolle werden gespeichert. Eigene, zusätzliche Rollen kommen mit dem Rollenmodell der Datenbank.",
      },
      {
        key: "anmeldung",
        title: "Anmeldung",
        why: "Wie streng die Anmeldung ist. Wer Mitglieder- oder Bankdaten sieht, sollte sich mit zweitem Faktor anmelden.",
        fields: zugriffAnmeldung,
        recht: VERWALTEN,
      },
    ],
  },
  {
    key: "datenschutz",
    title: "Datenschutz",
    group: "system",
    icon: "Lock",
    recht: VERWALTEN,
    tabs: [
      {
        key: "fristen",
        title: "Fristen",
        why: "Wie lange der Verein Daten behält. Die DSGVO verlangt Löschung, sobald der Zweck entfällt — clubhub erinnert daran oder erledigt es.",
        fields: datenschutzFristen,
      },
      {
        key: "verantwortliche",
        title: "Verantwortliche",
        why: "Wer im Verein für den Datenschutz ansprechbar ist und wo die Erklärung steht, auf die Formulare und Mitgliederbereich verweisen.",
        fields: datenschutzVerantwortlich,
        note: "Den Vertrag zur Auftragsverarbeitung mit der Ansichtssache stellt clubhub hier zum Herunterladen bereit, sobald er unterschrieben vorliegt.",
      },
    ],
  },
  {
    key: "schnittstellen",
    title: "Schnittstellen",
    group: "system",
    icon: "Plug",
    recht: VERWALTEN,
    aendern: API,
    tabs: [
      {
        key: "api",
        title: "API-Schlüssel",
        why: "clubhub hat eine offene REST-Schnittstelle. Ein Schlüssel erlaubt einem anderen Programm — Verbandssoftware, Website, Zapier —, genau die freigegebenen Daten zu lesen oder zu schreiben.",
        panel: "api-schluessel",
      },
      {
        key: "webhooks",
        title: "Webhooks",
        why: "clubhub meldet Ereignisse sofort an eine Adresse: neues Mitglied, Zahlung eingegangen, Zusage zum Termin.",
        panel: "webhooks",
      },
    ],
  },
  {
    key: "daten",
    title: "Daten",
    group: "system",
    icon: "Database",
    recht: VERWALTEN,
    tabs: [
      {
        key: "ablage",
        title: "Dateiablage",
        why: "Wo Dokumente, Bilder und Belege liegen. Der Speicher hat keine Obergrenze.",
        fields: datenAblage,
      },
      {
        key: "export",
        title: "Export und Nutzung",
        why: "Die Daten gehören dem Verein. Alles lässt sich jederzeit vollständig mitnehmen — ohne Staffel, ohne Obergrenze.",
        panel: "export",
      },
      {
        key: "protokoll",
        title: "Protokoll",
        why: "Wer wann was geändert hat — an Mitgliedern, Beiträgen, Rechten und an diesen Einstellungen.",
        panel: "protokoll",
      },
    ],
  },
];

// ── Mein Konto ──────────────────────────────────────────────
//
// Bis 24.10.2026 stand das als Bereich „Mein Zugang“ in den
// Einstellungen. Das war eine Vermischung: die Einstellungen sind
// der Verein, das Konto ist die Person — wer seine Erinnerungen
// abbestellt, verwaltet nicht den Verein. Der Weg hierher ist der
// eigene Name unten in der Leiste (wie im Agent). Gebaut wird es
// aus denselben Teilen: Felder, Formular, Speichern, Suche.

export const ACCOUNT_PATH = "/mein-konto";

export const ACCOUNT_AREA: SettingsArea = {
  key: "mein-konto",
  title: "Mein Konto",
  group: "persoenlich",
  icon: "CircleUserRound",
  personal: true,
  tabs: [
    {
      key: "profil",
      title: "Profil",
      why: "Womit deine Mails aus clubhub enden. Das stellst du selbst ein, nicht der Vorstand.",
      note: "Name, Anschrift und Einwilligungen gehören zu deinem Eintrag als Mitglied — die stehen unter Mein Bereich › Profil.",
      fields: kontoProfil,
    },
    {
      key: "benachrichtigungen",
      title: "Benachrichtigungen",
      // Auch im Mitgliederbereich (`/mein-bereich/konto`) — deshalb steht
      // die Anrede hier und nicht im Profil: du oder Sie waehlt jedes Mitglied.
      why: "Wie clubhub dich anspricht und worüber es dir schreibt. Rechnungen, Mahnungen und Einladungen zur Mitgliederversammlung kommen immer.",
      fields: kontoBenachrichtigungen,
    },
    {
      key: "anmeldung",
      title: "Anmeldung",
      why: "Passwort, Passkeys und zweiter Faktor — und auf welchen Geräten du gerade angemeldet bist.",
      panel: "anmeldung-persoenlich",
    },
  ],
};

/** Einstellungen und Konto — fuer alles, was ein Feld oder Register finden muss. */
export const ALL_AREAS: SettingsArea[] = [...SETTINGS_AREAS, ACCOUNT_AREA];

// ── Zugriff ─────────────────────────────────────────────────

export function findArea(key: string): SettingsArea | undefined {
  return ALL_AREAS.find((area) => area.key === key);
}

export function findTab(area: SettingsArea, key: string): SettingsTab | undefined {
  return area.tabs.find((tab) => tab.key === key);
}

/** Jedes Feld mit seinem Ort — Grundlage von Suche, Pruefung und Speichern. */
export function allFields(): { area: SettingsArea; tab: SettingsTab; field: SettingsField }[] {
  return ALL_AREAS.flatMap((area) =>
    area.tabs.flatMap((tab) => (tab.fields ?? []).map((field) => ({ area, tab, field }))),
  );
}

export function findField(key: string): SettingsField | undefined {
  return allFields().find((entry) => entry.field.key === key)?.field;
}

/** Die Standards aus dem Code — der Rueckfall, wenn nichts gesetzt ist. */
export function defaultValues(): SettingsValues {
  const values: SettingsValues = {};
  for (const { field } of allFields()) {
    if (field.default !== undefined) values[field.key] = field.default;
  }
  return values;
}

export function tabPath(area: SettingsArea, tab: SettingsTab): string {
  return area.key === ACCOUNT_AREA.key ? `${ACCOUNT_PATH}/${tab.key}` : `/einstellungen/${area.key}/${tab.key}`;
}

// ── Wer was sehen und aendern darf ──────────────────────────
//
// `darf` beantwortet eine Frage nach EINEM Recht — die Seite reicht
// `ctx.can` aus `requireClub()` hinein, der Browser eine Menge.

export type Darf = (permission: Permission) => boolean;

function einesVon(rechte: readonly Permission[] | undefined, darf: Darf): boolean {
  return !rechte || rechte.some((recht) => darf(recht));
}

/** Darf, wer diese Rechte hat, das Register sehen? */
export function registerErlaubt(area: SettingsArea, tab: SettingsTab, darf: Darf): boolean {
  if (area.personal) return true;
  return einesVon(tab.recht ?? area.recht ?? ["settings.manage"], darf);
}

/** Darf er es aendern? Sehen ist Voraussetzung. */
export function registerAenderbar(area: SettingsArea, tab: SettingsTab, darf: Darf): boolean {
  if (area.personal) return true;
  if (!registerErlaubt(area, tab, darf)) return false;
  return einesVon(tab.aendern ?? area.aendern ?? tab.recht ?? area.recht ?? ["settings.manage"], darf);
}

export function erlaubteRegister(area: SettingsArea, darf: Darf): SettingsTab[] {
  return area.tabs.filter((tab) => registerErlaubt(area, tab, darf));
}

export function ersterErlaubterPfad(area: SettingsArea, darf: Darf): string | null {
  const tab = erlaubteRegister(area, darf)[0];
  return tab ? tabPath(area, tab) : null;
}

/** Wohin der Einstieg fuehrt: das erste erlaubte Register — wer keines sehen darf, landet im eigenen Konto. */
export function einstiegsPfad(darf: Darf): string {
  for (const area of SETTINGS_AREAS) {
    const pfad = ersterErlaubterPfad(area, darf);
    if (pfad) return pfad;
  }
  return ACCOUNT_PATH;
}

/** Das Register, in dem ein Feld steht — fuer das Speichern, das nur Felder SEINES Registers annimmt. */
export function felderDesRegisters(areaKey: string, tabKey: string): SettingsField[] {
  const area = findArea(areaKey);
  const tab = area && findTab(area, tabKey);
  return tab?.fields ?? [];
}
